<script setup lang="ts">
import { PageHeader, PageShell } from '@taler/ui'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { canManageMembership, MembershipScreen, useMembership } from '@/features/membership'
import { useSession } from '@/app/session'

const { t, locale } = useI18n()
const session = useSession()

const canManage = computed(() => canManageMembership(session.can))
const uiLocale = computed(() => locale.value as 'en' | 'hu')

const membership = useMembership({
  ledgerId: () => session.activeLedger.value?.id,
  enabled: () => canManage.value,
})
</script>

<template>
  <PageShell class="gap-6">
    <PageHeader :title="t('membership.title')" />

    <p v-if="session.isLoading.value" role="status">{{ t('app.loading') }}</p>

    <p v-else-if="session.activeLedger.value === undefined" class="text-[color:var(--color-text-muted)]">
      {{ t('membership.noLedger') }}
    </p>

    <p v-else-if="!canManage" class="text-[color:var(--color-text-muted)]">
      {{ t('membership.noAccess') }}
    </p>

    <MembershipScreen
      v-else
      :members="membership.members.value"
      :invitations="membership.invitations.value"
      :is-loading="membership.isLoading.value"
      :error="membership.error.value"
      :is-adding-member="membership.isAddingMember.value"
      :add-member-error="membership.addMemberError.value"
      :changing-role-subject="membership.changingRoleSubject.value"
      :change-role-error="membership.changeRoleError.value"
      :removing-subject="membership.removingSubject.value"
      :remove-error="membership.removeError.value"
      :is-inviting="membership.isInviting.value"
      :invite-error="membership.inviteError.value"
      :revoking-id="membership.revokingId.value"
      :revoke-error="membership.revokeError.value"
      :locale="uiLocale"
      @add-member="membership.addMember($event)"
      @change-role="membership.changeRole($event)"
      @remove-member="membership.remove($event)"
      @invite="membership.invite($event)"
      @revoke="membership.revoke($event)"
      :retrying="membership.retrying.value"
      @retry="membership.retry()"
    />
  </PageShell>
</template>
